import type { DonneesArbre, Personne } from './types.js';

const majuscule = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** « Marc B. » ; sans prénom connu, le lien de parenté en clair (« Fille de Lucien (prénom inconnu) »). */
export function nomAffiche(p: Personne): string {
  const prenom = p.firstNames.trim().split(/\s+/)[0] ?? '';
  if (prenom === '') return majuscule(p.relationLabel ?? 'Personne à identifier');
  return `${prenom} ${p.lastName}`.trim();
}

const sansDoublon = (ids: string[]): string[] => [...new Set(ids)];

export function enfantsDe(d: DonneesArbre, id: string): string[] {
  return sansDoublon(d.relations.filter((r) => r.kind === 'filiation' && r.personAId === id).map((r) => r.personBId));
}

export function parentsDe(d: DonneesArbre, id: string): string[] {
  return sansDoublon(d.relations.filter((r) => r.kind === 'filiation' && r.personBId === id).map((r) => r.personAId));
}

export function conjointsDe(d: DonneesArbre, id: string): string[] {
  return sansDoublon(
    d.relations
      .filter((r) => r.kind === 'union' && (r.personAId === id || r.personBId === id))
      .map((r) => (r.personAId === id ? r.personBId : r.personAId)),
  );
}

function generationParFiliation(d: DonneesArbre, id: string, vus: Set<string>): number | null {
  if (vus.has(id)) return null;
  const parents = parentsDe(d, id);
  if (parents.length === 0) return null;
  const suivants = new Set(vus).add(id);
  const niveaux = parents.map((p) => generationParFiliation(d, p, suivants) ?? 0);
  return Math.max(...niveaux) + 1;
}

/** Auteurs à 0 ; un enfant a la génération de son parent plus un ; un conjoint prend celle de son partenaire. */
export function generationDe(d: DonneesArbre, id: string): number {
  const propre = generationParFiliation(d, id, new Set());
  if (propre !== null) return propre;
  for (const c of conjointsDe(d, id)) {
    const g = generationParFiliation(d, c, new Set([id]));
    if (g !== null) return g;
  }
  return 0;
}

/** Les auteurs : les personnes de génération 0, dans l'ordre du dossier. */
export function auteurs(d: DonneesArbre): string[] {
  return d.personnes.filter((p) => parentsDe(d, p.id).length === 0 && generationDe(d, p.id) === 0).map((p) => p.id);
}

interface Fraction {
  n: bigint;
  d: bigint;
}

const pgcd = (a: bigint, b: bigint): bigint => (b === 0n ? a : pgcd(b, a % b));

function reduire(f: Fraction): Fraction {
  const g = pgcd(f.n, f.d) || 1n;
  return { n: f.n / g, d: f.d / g };
}

const somme = (liste: Fraction[]): Fraction =>
  reduire(liste.reduce((acc, f) => ({ n: acc.n * f.d + f.n * acc.d, d: acc.d * f.d }), { n: 0n, d: 1n }));

const texteFraction = (f: Fraction): string => (f.d === 1n ? `${f.n}` : `${f.n}/${f.d}`);

const depuisContrat = (f: { numerator: string; denominator: string }): Fraction => ({
  n: BigInt(f.numerator),
  d: BigInt(f.denominator),
});

/** Quote-part directe d'une personne, en fraction exacte (« 1/3 »), ou nulle. */
export function quotePart(d: DonneesArbre, id: string): string | null {
  const parts = d.devolution.shares.filter((s) => s.personId === id).map((s) => depuisContrat(s.share));
  return parts.length === 0 ? null : texteFraction(somme(parts));
}

/** Total des parts reçues par les personnes de `ids`, plus les réserves dont l'intitulé nomme `prenomBranche`. */
export function quotePartBranche(d: DonneesArbre, ids: readonly string[], prenomBranche: string): string | null {
  const parts = [
    ...d.devolution.shares.filter((s) => ids.includes(s.personId)).map((s) => depuisContrat(s.share)),
    ...d.devolution.reserves
      .filter((r) => prenomBranche !== '' && r.branchLabel.includes(prenomBranche))
      .map((r) => depuisContrat(r.share)),
  ];
  return parts.length === 0 ? null : texteFraction(somme(parts));
}

/** Total des parts qui passent par `viaId` (transmission ou représentation). */
export function partTransmiseVia(d: DonneesArbre, viaId: string): string | null {
  const parts = d.devolution.shares.filter((s) => s.viaPersonId === viaId).map((s) => depuisContrat(s.share));
  return parts.length === 0 ? null : texteFraction(somme(parts));
}

/** La personne, ses descendants et leurs conjoints : les membres d'une branche. */
export function membresDeBranche(d: DonneesArbre, racine: string): string[] {
  const vus: string[] = [];
  const visite = (id: string): void => {
    if (vus.includes(id)) return;
    vus.push(id);
    for (const c of conjointsDe(d, id)) if (!auteurs(d).includes(c)) visite(c);
    for (const e of enfantsDe(d, id)) visite(e);
  };
  visite(racine);
  return vus;
}

export const personneParId = (d: DonneesArbre, id: string): Personne | undefined => d.personnes.find((p) => p.id === id);

export const anneeDeces = (p: Personne): string | null => (p.deathDate ? p.deathDate.slice(0, 4) : null);
